import { Capacitor, registerPlugin } from '@capacitor/core';

// Le pont avec la coque Android (`android/`, plugins Kotlin `NotaSession` et `NotaPush`, specs 16 et 18). C'est le
// seul module qui connaît Capacitor. Dans la coque, le front remet son jeton au natif — les
// widgets et le push (specs 17 et 18) n'auront jamais à demander le mot de passe. Le jeton ne
// voyage que dans ce sens. Dans un navigateur ou la PWA iOS, rien de ceci ne s'exécute.

interface NotaSessionPlugin {
  setToken(options: { token: string }): Promise<void>;
  clearToken(): Promise<void>;
}

export const inNativeShell = Capacitor.isNativePlatform();

// Le front vient de nota.lyodge.com : il arrive dans un APK installé avant la spec 18, qui n'a
// pas le plugin. Là, la modale demande la mise à jour au lieu d'échouer sur chaque appel.
export const nativePushAvailable = inNativeShell && Capacitor.isPluginAvailable('NotaPush');

const NotaSession = registerPlugin<NotaSessionPlugin>('NotaSession');

// Un pont qui échoue ne doit pas casser la session web : le front redonne le jeton à chaque
// démarrage, le natif finit par l'avoir.
export function shareTokenWithShell(token: string): void {
  if (inNativeShell) void NotaSession.setToken({ token }).catch(() => {});
}

// Logout voulu : le push natif se désabonne avec le jeton encore en place (spec 18) — le natif
// lit le jeton dès l'appel de disable(), avant le clearToken() qui le suit sur le même fil.
// Session expirée (401) : le push reste, l'endpoint est ré-enregistré au prochain setToken().
export function clearTokenInShell({ disablePush }: { disablePush: boolean }): void {
  if (!inNativeShell) return;
  if (disablePush && nativePushAvailable) void NotaPush.disable().catch(() => {});
  void NotaSession.clearToken().catch(() => {});
}

// Le push de la coque (plugin Kotlin `NotaPush`, spec 18) : UnifiedPush, sans Google. La
// modale Notifications l'utilise à la place de `useNotifications` dans la coque.
export interface NativePushStatus {
  /** Paquet du distributeur UnifiedPush (ntfy) ; `null` : aucun n'est installé. */
  distributor: string | null;
  permission: 'granted' | 'denied' | 'prompt';
  /** Vrai quand le serveur a confirmé l'abonnement. */
  subscribed: boolean;
  /** Le dernier refus (endpoint rejeté, distributeur qui refuse l'inscription). */
  error: string | null;
}

interface NotaPushPlugin {
  status(): Promise<NativePushStatus>;
  enable(): Promise<NativePushStatus>;
  disable(): Promise<NativePushStatus>;
  openSettings(): Promise<void>;
}

export const NotaPush = registerPlugin<NotaPushPlugin>('NotaPush');

/** iPhone, iPod, iPad — y compris l'iPad qui se présente comme un Mac. */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}
